<script lang="ts" setup>
import Button from 'primevue/button'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import StatusTag from '@/components/table/StatusTag.vue'
import TableFilter from '@/components/table/TableFilter.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import PageHeader from '@/components/global/page-header/PageHeader.vue'

import { onMounted, ref, computed } from 'vue'
import { Icon } from '@iconify/vue'
import { RouterLink, useRouter } from 'vue-router'
import { getCustomerList } from '@/api/customers'

const router = useRouter()
const customerList = ref<any>([])
const loading = ref<boolean>(true)
const searchQuery = ref('')
const statusFilters = ref<string[]>([])

const filteredCustomerList = computed(() => {
  const query = searchQuery.value.toLowerCase()
  let filtered = customerList.value.filter(
      (customer: { businessName: string; salesContactEmail: string; salesContactTelephone: string }) =>
          customer.businessName.toLowerCase().includes(query) ||
          customer.salesContactEmail.toLowerCase().includes(query) ||
          customer.salesContactTelephone.toLowerCase().includes(query)
  )

  if (statusFilters.value.length > 0 && !statusFilters.value.includes('all')) {
    filtered = filtered.filter((customer: { status?: string }) => {
      const customerStatus = customer.status || 'active'
      return statusFilters.value.includes(customerStatus)
    })
  }

  return filtered
})

const handleFilterChange = (filters: string[]) => {
  statusFilters.value = filters
}

onMounted(async () => {
  customerList.value = await getCustomerList()
  loading.value = false
})
</script>

<template>
  <PageHeader
      title="Customers"
      :description="`Total: ${customerList.length} | Filtered: ${filteredCustomerList.length}`"
      buttonText="Add Customer"
      buttonIcon="fluent:person-add-16-filled"
      :buttonClick="() => router.push('/customers/add')"
  />
  <DataTable
      :value="filteredCustomerList"
      paginator
      :rows="10"
      tableStyle="min-width: 50rem"
      stripedRows
      paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink"
      :rowsPerPageOptions="[10, 25]"
      currentPageReportTemplate="Showing {first} to {last} of {totalRecords} customers"
      :loading="loading"
      :pt="{
      table: loading && 'min-h-[30rem]'
    }"
      :pt-options="{
      mergeProps: true
    }"
  >
    <template #empty> <p class="text-center">No customers found.</p> </template>

    <template #header>
      <div class="flex items-center justify-between font-normal">
        <TableSearchInput v-model:searchQuery="searchQuery" />

        <TableFilter
            :filter-options="[
              { value: 'all', label: 'All', id: 'customer-status-all' },
              { value: 'active', label: 'Active', id: 'customer-status-active' },
              { value: 'inactive', label: 'Inactive', id: 'customer-status-inactive' }
            ]"
            filter-title="Filter Customer Status"
            @filter-change="handleFilterChange"
        />
      </div>
    </template>

    <Column field="businessName" header="Business Name" sortable>
      <template #body="slotProps">
        <RouterLink
            :to="`/customers/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.businessName }}
        </RouterLink>
      </template>
    </Column>
    <Column field="salesContactEmail" header="Sales Contact Email" sortable />
    <Column field="salesContactTelephone" header="Sales Contact Telephone Number" sortable />
    <Column field="customerAccountReference" header="Customer Account Reference" sortable/>
    <Column field="status" header="Status" sortable>
      <template #body="slotProps">
        <StatusTag :status="slotProps.data.status || 'active'" />
      </template>
    </Column>
    <Column field="action">
      <template #body="slotProps">
        <RouterLink
            :to="`/customers/${slotProps.data.id}`"
            class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span> More Details</span>
          <Icon
              icon="material-symbols:more-up"
              class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>

    <!------------------------------------------ pagination---------------------------------------------------->
    <template #paginatorprevpagelinkicon>
      <Button size="small" class="space-x-1 items-center" outlined severity="secondary">
        <Icon icon="material-symbols:chevron-left" class="text-2xl" />
        <span>Previous</span>
      </Button>
    </template>
    <template #paginatornextpagelinkicon>
      <Button size="small" outlined severity="secondary" class="space-x-1 items-center">
        <span>Next</span>
        <Icon icon="material-symbols:chevron-right" class="text-2xl" />
      </Button>
    </template>
  </DataTable>
</template>

<style></style>
